import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { WithWrite } from '../../lib/rbac';
import { settingsApi } from '../../services/settings';

type CompanyForm = {
  contactName: string;
  address1: string;
  postalCode: string;
  city: string;
  country: string;
  orgNr: string;
  vatNr: string;
  seat: string;
  hasFSkatt: boolean;
  bankgiro: string;
  email: string;
  web: string;
  phone: string;
  beekeepingStartDate: string;
  footerText: string;
};

const defaultForm: CompanyForm = {
  contactName: '',
  address1: '',
  postalCode: '',
  city: '',
  country: 'SE',
  orgNr: '',
  vatNr: '',
  seat: '',
  hasFSkatt: true,
  bankgiro: '',
  email: '',
  web: '',
  phone: '',
  beekeepingStartDate: '',
  footerText: '',
};

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const pos = result.indexOf(',');
      resolve(pos >= 0 ? result.slice(pos + 1) : result);
    };
    reader.onerror = () => reject(new Error('Kunde inte läsa filen'));
    reader.readAsDataURL(file);
  });
}

export function CompanySettingsPage() {
  const toast = useToast();
  const [form, setForm] = useState<CompanyForm>(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoMissing, setLogoMissing] = useState(false);

  const logoUrl = useMemo(() => `${settingsApi.getCompanyLogoUrl()}?v=${logoVersion}`, [logoVersion]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await settingsApi.getBusinessPrintSettings();
      setForm({
        contactName: data.contactName ?? '',
        address1: data.address1 ?? data.contactAddress ?? '',
        postalCode: data.postalCode ?? '',
        city: data.city ?? '',
        country: data.country ?? 'SE',
        orgNr: data.orgNr ?? '',
        vatNr: data.vatNr ?? '',
        seat: data.seat ?? '',
        hasFSkatt: data.hasFSkatt ?? true,
        bankgiro: data.bankgiro ?? '',
        email: data.email ?? '',
        web: data.web ?? '',
        phone: data.phone ?? '',
        beekeepingStartDate: data.beekeepingStartDate ?? '',
        footerText: data.footerText ?? '',
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte hämta företagsinformation');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const getLegacyContactAddress = (current: CompanyForm): string => {
    const line1 = current.address1.trim();
    const line2 = [current.postalCode.trim(), current.city.trim()].filter(Boolean).join(' ');
    const line3 = current.country.trim();
    return [line1, line2, line3].filter(Boolean).join(', ');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.updateBusinessPrintSettings({
        contactName: form.contactName || null,
        contactAddress: getLegacyContactAddress(form) || null,
        address1: form.address1 || null,
        postalCode: form.postalCode || null,
        city: form.city || null,
        country: form.country || 'SE',
        orgNr: form.orgNr || null,
        vatNr: form.vatNr || null,
        seat: form.seat || null,
        hasFSkatt: form.hasFSkatt,
        bankgiro: form.bankgiro || null,
        email: form.email || null,
        web: form.web || null,
        phone: form.phone || null,
        beekeepingStartDate: form.beekeepingStartDate || null,
        footerText: form.footerText || null,
      });
      toast.success('Företagsinformation sparad');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara företagsinformation');
    } finally {
      setSaving(false);
    }
  };

  const onLogoSelected = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    if (!file) {
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Endast PNG, JPEG eller WEBP stöds');
      return;
    }
    setUploadingLogo(true);
    try {
      const contentBase64 = await toBase64(file);
      await settingsApi.uploadCompanyLogo({
        filename: file.name,
        mimeType: file.type,
        contentBase64,
      });
      setLogoMissing(false);
      setLogoVersion((v) => v + 1);
      toast.success('Logga uppladdad');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda upp logga');
    } finally {
      setUploadingLogo(false);
      ev.target.value = '';
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-3">
      <h1 className="text-lg font-semibold">Företagsinformation</h1>
      <p className="text-sm text-gray-600">
        Fyll i organisationsnummer, kontaktuppgifter och ladda upp företagslogga för utskrifter.
      </p>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <section className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
          <label className="block text-sm font-medium">Kontaktnamn</label>
          <input className="w-full" value={form.contactName} onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))} />
          <label className="block text-sm font-medium">Postadress</label>
          <input className="w-full" value={form.address1} onChange={(e) => setForm((f) => ({ ...f, address1: e.target.value }))} />
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[120px_1fr]">
            <div>
              <label className="block text-sm font-medium">Postnummer</label>
              <input className="w-full" value={form.postalCode} onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium">Ort</label>
              <input className="w-full" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
            </div>
          </div>
          <label className="block text-sm font-medium">Land</label>
          <input className="w-full" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} />
          <label className="block text-sm font-medium">Organisationsnummer</label>
          <input className="w-full" value={form.orgNr} onChange={(e) => setForm((f) => ({ ...f, orgNr: e.target.value }))} />
          <label className="block text-sm font-medium">Momsreg.nr</label>
          <input className="w-full" value={form.vatNr} onChange={(e) => setForm((f) => ({ ...f, vatNr: e.target.value }))} />
          <label className="block text-sm font-medium">Säte</label>
          <input className="w-full" value={form.seat} onChange={(e) => setForm((f) => ({ ...f, seat: e.target.value }))} />
          <label className="flex items-center gap-2 pt-1">
            <input type="checkbox" checked={form.hasFSkatt} onChange={(e) => setForm((f) => ({ ...f, hasFSkatt: e.target.checked }))} />
            <span className="text-sm font-medium">F-skatt</span>
          </label>
          <label className="block text-sm font-medium">Bankgiro</label>
          <input className="w-full" value={form.bankgiro} onChange={(e) => setForm((f) => ({ ...f, bankgiro: e.target.value }))} />
          <label className="block text-sm font-medium">E-post</label>
          <input className="w-full" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          <label className="block text-sm font-medium">Webbplats</label>
          <input className="w-full" value={form.web} onChange={(e) => setForm((f) => ({ ...f, web: e.target.value }))} />
          <label className="block text-sm font-medium">Telefon</label>
          <input className="w-full" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          <label className="block text-sm font-medium">Startdatum för biodlingen</label>
          <input
            type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD"
            className="w-full"
            value={form.beekeepingStartDate}
            onChange={(e) => setForm((f) => ({ ...f, beekeepingStartDate: e.target.value }))}
          />
          <label className="block text-sm font-medium">Sidfotstext</label>
          <input className="w-full" value={form.footerText} onChange={(e) => setForm((f) => ({ ...f, footerText: e.target.value }))} />
          <WithWrite moduleCode="settings">
            <button type="button" className="btn btn-success px-2 py-1" onClick={handleSave} disabled={saving}>
              {saving ? 'Sparar...' : 'Spara'}
            </button>
          </WithWrite>
        </section>
        <section className="rounded border border-gray-200 bg-white p-3 space-y-2">
          <h2 className="text-base font-semibold">Företagslogga</h2>
          <div className="rounded border border-dashed border-gray-300 p-3 min-h-40 flex items-center justify-center bg-gray-50">
            <img
              src={logoUrl}
              alt="Företagslogga"
              className={`max-h-36 max-w-full object-contain ${logoMissing ? 'hidden' : ''}`}
              onError={() => {
                setLogoMissing(true);
              }}
              onLoad={() => {
                setLogoMissing(false);
              }}
            />
            {logoMissing && <span className="text-sm text-gray-500">Ingen logga uppladdad ännu</span>}
          </div>
          <WithWrite moduleCode="settings">
            <label className="inline-flex cursor-pointer items-center rounded border border-orange-500 px-3 py-1 text-sm hover:bg-orange-50">
              {uploadingLogo ? 'Laddar upp...' : 'Ladda upp logga'}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogoSelected} disabled={uploadingLogo} />
            </label>
          </WithWrite>
          <p className="text-xs text-gray-500">Stöd: PNG, JPEG, WEBP. Max 2 MB.</p>
        </section>
      </div>
    </div>
  );
}
